import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { WelcomeStep } from "./WelcomeStep/WelcomeStep";
import {
	BusyIndicator,
	Form,
	FormItem,
	FormattedText,
	TextInput,
	ValidationError,
	useConfirmationDialog
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	ConfigureSharePointUrlResult,
	SaveSharePointUrlModel,
	SharePointAuthenticationType,
	SharePointSettingsViewModel,
	configureSharePointUrl,
	getSharePointSettingsViewModel,
	tryDetectSharePointUrl
} from "client/Api/SharePointSettings/sharepointSettings";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	IRootValidation,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined,
	isUrl
} from "client/Validation/rules";
import {
	ensureNotNil,
	isNotNil
} from "client/Misc/utils";
import { ExternalServiceConfiguredEvents, publish } from "client_legacy/WelcomeScreen/externalServiceConfiguredEvents";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { SharePointAuthenticationTypeFormItem } from "../Settings/SharePoint/SharePointAuthenticationTypeFormItem";
import image from "Content/Images/sharepoint-step.svg";
import { ContentLayout } from "./ContentLayout";
import { never } from "client_legacy/ndaw/errors";
import { HttpRequestError } from "client/Api/httpClient";
import { PermissionChangeErrorMessage } from "./PermissionChangeErrorMessage";

export type SharePointStepProps = {
	onNext: () => void;
};

export const SharePointStep = (props: SharePointStepProps): ReactElement => {
	const {
		onNext
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [formData, setFormData] = useState<SaveSharePointUrlModel | null>(null);
	const [availableAuthenticationTypes, setAvailableAuthenticationTypes] = useState<readonly SharePointAuthenticationType[]>([]);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();

	const formHandler: IFormHandler<SaveSharePointUrlModel | null> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	useEffect(
		(): (() => void) => {
			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				let viewModel: SharePointSettingsViewModel | null = null;
				try {
					viewModel = await getSharePointSettingsViewModel(signal);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error("Failed to load the SharePoint settings", e);
				}

				const availableAuthenticationTypes: readonly SharePointAuthenticationType[] = viewModel?.availableAuthenticationTypes ?? [];

				setAvailableAuthenticationTypes(availableAuthenticationTypes);

				if (viewModel
					&& viewModel.isEnabled
					&& viewModel.url
				) {
					setFormData({
						authenticationType: availableAuthenticationTypes.includes(viewModel.authenticationType)
							? viewModel.authenticationType
							: availableAuthenticationTypes[0] ?? null,
						sharePointUrl: viewModel.url,
						shouldDisableSiteAvailability: false
					});
				} else {
					try {
						const url: string | null = await tryDetectSharePointUrl(signal);

						setFormData({
							authenticationType: availableAuthenticationTypes[0] ?? null,
							sharePointUrl: url ?? "",
							shouldDisableSiteAvailability: false
						});

					} catch (e) {
						if (isCancelledError(e)) {
							return;
						}
						console.error(e);
					}
				}

				setIsBusy(false);
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);
			const validation: IRootValidation<SaveSharePointUrlModel> = formHandler.validation;

			try {

				const validationResult: InputValidationResult = await validation.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const model: SaveSharePointUrlModel = ensureNotNil(formData);

				let isStillSaving: boolean = true;

				while (isStillSaving) {
					const configurationResult: ConfigureSharePointUrlResult = await configureSharePointUrl(
						ensureNotNil(formData),
						globalCancellation
					);

					isStillSaving = false;

					switch (configurationResult) {
						case ConfigureSharePointUrlResult.Successful:
							publish(ExternalServiceConfiguredEvents.sharePoint, undefined);
							onNext();
							break;
						case ConfigureSharePointUrlResult.NeedToDisableSiteAvailability:
							const confirmationResult: TryResult<void> = await openConfirmationDialog({
								title: localize("_AreYouSureYouWantToChangeTheSharePointURL"),
								children: (
									<FormattedText
										text={localize("_NoteCurrentlySettingsRelatedToTheAvailabilityOfSharePointDescription")}
										thingsToWrap={[
											{
												blockId: "Note",
												wrap: (children) => (
													<b>
														{children}
													</b>
												)
											}
										]}
									/>
								),
								confirmText: localize("_ChangeUrl"),
								trackingId: "disable-sharepoint-site-availability-confirmation-dialog"
							});

							if (confirmationResult.wasSuccessful) {
								model.shouldDisableSiteAvailability = true;
								isStillSaving = true;
							} else {
								setIsBusy(false);
							}

							break;
						default:
							never(configurationResult);
					}
				}

			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);

				let message: string | ReactElement | null = null;

				if (e instanceof HttpRequestError) {
					switch(e.statusCode) {
						case 403:
							message = <PermissionChangeErrorMessage />;
							break;
						default:
							if (!validation.tryProcessServerError(e)) {
								message = localize("_GeneralErrorMessage");
							}
							break;
					}
				}

				if (message) {
					alertManager.showError(message);
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onNext,
			openConfirmationDialog
		]
	);

	return (
		<>
			<WelcomeStep.Left trackingId="sharepoint-step">
				<ContentLayout
					title={localize("_FoldersAndFiles")}
					explanation={
						formHandler.value
							? (
								<FormattedText
									text={localize("_SharePointStepExplanation")}
									thingsToReplace={[]}
								/>
							)
							: undefined
					}
				>
					{isBusy && (
						<BusyIndicator />
					)}

					{formHandler.whenType(
						isNotNil,
						(handler): ReactElement => (
							<>
								<Form onSubmit={submitHandler}>
									<FormItem
										label={localize("_CompanySharePointUrl")}
										isRequired
									>
										<TextInput
											{...handler.for("sharePointUrl").attachToTextInput()}
											placeholder="https://company.sharepoint.com/"
										/>

										<ValidationError {...handler.for("sharePointUrl").attachToValidationError()} />
									</FormItem>

									<SharePointAuthenticationTypeFormItem
										attachable={handler.for("authenticationType")}
										availableAuthenticationTypes={availableAuthenticationTypes}
									/>
								</Form>

								<ContentLayout.Buttons
									onContinue={submitHandler}
									onSkip={onNext}
								/>
							</>
						)
					)}

				</ContentLayout>
			</WelcomeStep.Left>

			<WelcomeStep.Right
				imageUrl={image}
				text={localize("_EasyAccessToAllYourDocumentsWithinTheWorkspace")}
			/>

			{confirmationDialog}
		</>
	);
};
SharePointStep.displayName = "SharePointStep";

const validationConfiguration: IValidationConfiguration<SaveSharePointUrlModel | null>
	= createValidationConfiguration<SaveSharePointUrlModel | null>();

validationConfiguration.whenType(isNotNil)
	.forScalar("sharePointUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: true,
			allowQueryString: false,
			alternativeMessage: localize("_InvalidSharePointUrl")
		})
	);

validationConfiguration.whenType(isNotNil)
	.forScalar("authenticationType")
	.add(
		isNotNullNorUndefined()
	);
